import { CommonModule } from '@angular/common';
import { Component, input, output, ChangeDetectionStrategy } from '@angular/core';
import { IconWeight } from '../../models';
import { GsIconComponent } from '../gs-icon/gs-icon.component';

/** Type for all the defined and useable color presets */
export type GsButtonColor = 'd-primary' | 'd-secondary' | 'd-light' | 'd-red' | 'd-green';

/** Interface fo the pressed-event outputted by the component */
export interface GsButtonPressedEvent {
  event: MouseEvent;
}

/**
 * A simple button component, mostly used for the designer
 * Create a button with a label and an optional icon using the correct design language
 */
@Component({
  selector: 'gs-button',
  imports: [CommonModule, GsIconComponent],
  templateUrl: './gs-button.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: inline-block;
      }
    `
  ]
})
export class GsButtonComponent {
  // Inputs & Models
  /** The label/text on the button */
  readonly label = input<string>();
  /** The color of the button (is predefined in component) */
  readonly color = input<GsButtonColor>();
  /** The icon name to display on the button */
  readonly iconName = input<string>();
  /** The icon type to display on the button */
  readonly iconWeight = input<IconWeight>('light');
  /** Whether the button is disabled */
  readonly disabled = input(false);
  /** The size of the button */
  readonly size = input<'sm' | 'md' | 'lg'>('md');
  /** The minimum width of the button using tailwind-class min-w */
  readonly minWidth = input<'none' | '32'>('none');
  /** A boolean to indicate if the button is in a loading state */
  readonly loading = input(false);
  /** Optional href for external navigation; when provided, renders as anchor */
  readonly href = input<string>();
  /** Anchor target when using routerLink; defaults to _self */
  readonly target = input<'_self' | '_blank' | '_parent' | '_top'>('_self');

  // Outputs
  /** Event wich gets fired if the button got pressed */
  readonly pressed = output<GsButtonPressedEvent>();

  // Methods
  /**
   * Handles the click event on the button
   * @param event The click event
   */
  onClick(event: MouseEvent): void {
    if (this.disabled()) return;
    this.pressed.emit({ event });
  }
}
